/**
 * Hospital Indemnity Transform Functions
 *
 * Handles bi-directional transformation between API format (nested) and grid format (flat).
 */

import {
  transformTypeAmountToApi,
  transformTypeAmountFromApi,
  transformPreExistingConditionToApi,
  transformPreExistingConditionFromApi,
  shouldIncludeBenefitInApi,
} from "../shared/builders";

import type {
  HospitalIndemnityBenefitsApi,
  HospitalIndemnityBenefitsGrid,
} from "./types";

/**
 * Transform Hospital Indemnity benefits from grid format to API format
 */
export function transformHospitalIndemnityBenefitsToApi(
  flatBenefits: HospitalIndemnityBenefitsGrid,
): HospitalIndemnityBenefitsApi {
  const result: HospitalIndemnityBenefitsApi = {
    objectType: "hospitalIndemnity",
    employerContribution: flatBenefits.employerContribution,
  };

  // Hospital Admission
  if (shouldIncludeBenefitInApi(flatBenefits.hospitalAdmissionType)) {
    result.hospitalAdmission = transformTypeAmountToApi(
      flatBenefits.hospitalAdmissionType,
      flatBenefits.hospitalAdmissionAmount,
    ) as HospitalIndemnityBenefitsApi["hospitalAdmission"];
  }

  // ICU Admission
  if (shouldIncludeBenefitInApi(flatBenefits.icuAdmissionType)) {
    result.icuAdmission = transformTypeAmountToApi(
      flatBenefits.icuAdmissionType,
      flatBenefits.icuAdmissionAmount,
    ) as HospitalIndemnityBenefitsApi["icuAdmission"];
  }

  // Hospital Confinement
  if (shouldIncludeBenefitInApi(flatBenefits.hospitalConfinementType)) {
    result.hospitalConfinement = transformTypeAmountToApi(
      flatBenefits.hospitalConfinementType,
      flatBenefits.hospitalConfinementAmount,
    ) as HospitalIndemnityBenefitsApi["hospitalConfinement"];
  }

  // ICU Confinement
  if (shouldIncludeBenefitInApi(flatBenefits.icuConfinementType)) {
    result.icuConfinement = transformTypeAmountToApi(
      flatBenefits.icuConfinementType,
      flatBenefits.icuConfinementAmount,
    ) as HospitalIndemnityBenefitsApi["icuConfinement"];
  }

  // Wellness
  if (shouldIncludeBenefitInApi(flatBenefits.wellnessType)) {
    result.wellness = transformTypeAmountToApi(
      flatBenefits.wellnessType,
      flatBenefits.wellnessAmount,
    ) as HospitalIndemnityBenefitsApi["wellness"];
  }

  // Pre-Existing Condition
  if (shouldIncludeBenefitInApi(flatBenefits.preExistingConditionType)) {
    result.preExistingCondition = transformPreExistingConditionToApi(
      flatBenefits.preExistingConditionType,
      flatBenefits.preExistingConditionMonthsDetail,
      "none",
      "01/01",
    ) as HospitalIndemnityBenefitsApi["preExistingCondition"];
  }

  // Portability
  if (shouldIncludeBenefitInApi(flatBenefits.portability)) {
    result.portability = flatBenefits.portability;
  }

  // Annual Open Enrollment
  if (shouldIncludeBenefitInApi(flatBenefits.annualOpenEnrollment)) {
    result.annualOpenEnrollment = flatBenefits.annualOpenEnrollment;
  }

  // Extended (non-v5) fields — CD-1943. Sent as a full object (a full-replace
  // PATCH, like the rest of `benefits`) whenever at least one extended field
  // is set, so an edit to any other field on the plan doesn't drop the
  // extended values already on it. Omitted entirely when every extended
  // field is unset, so an edit that never touches these fields doesn't
  // create an empty extended row on the backend.
  const extended = {
    eligibility: flatBenefits.eligibility,
    twentyFourHourCoverage: flatBenefits.twentyFourHourCoverage,
    pregnancy: flatBenefits.pregnancy,
    newbornNurseryCare: flatBenefits.newbornNurseryCare,
    eligibilityHours: flatBenefits.eligibilityHours,
    pregnancyWaitingPeriodNote: flatBenefits.pregnancyWaitingPeriodNote,
  };
  if (
    Object.values(extended).some((value) => value !== undefined && value !== "")
  ) {
    result.extended = extended;
  }

  return result;
}

/**
 * Transform Hospital Indemnity benefits from API format to grid format
 */
export function transformHospitalIndemnityBenefitsFromApi(
  apiBenefits: HospitalIndemnityBenefitsApi,
): HospitalIndemnityBenefitsGrid {
  return {
    objectType: "hospitalIndemnity",
    employerContribution: apiBenefits.employerContribution,
    ...transformTypeAmountFromApi(
      apiBenefits.hospitalAdmission,
      "hospitalAdmission",
    ),
    ...transformTypeAmountFromApi(apiBenefits.icuAdmission, "icuAdmission"),
    ...transformTypeAmountFromApi(
      apiBenefits.hospitalConfinement,
      "hospitalConfinement",
    ),
    ...transformTypeAmountFromApi(apiBenefits.icuConfinement, "icuConfinement"),
    ...transformTypeAmountFromApi(apiBenefits.wellness, "wellness"),
    ...transformPreExistingConditionFromApi(apiBenefits.preExistingCondition),
    portability: apiBenefits.portability,
    annualOpenEnrollment: apiBenefits.annualOpenEnrollment,

    // Extended (non-v5) fields — CD-1943
    eligibility: apiBenefits.extended?.eligibility ?? undefined,
    twentyFourHourCoverage:
      apiBenefits.extended?.twentyFourHourCoverage ?? undefined,
    pregnancy: apiBenefits.extended?.pregnancy ?? undefined,
    newbornNurseryCare: apiBenefits.extended?.newbornNurseryCare ?? undefined,
    eligibilityHours: apiBenefits.extended?.eligibilityHours ?? undefined,
    pregnancyWaitingPeriodNote:
      apiBenefits.extended?.pregnancyWaitingPeriodNote ?? undefined,
  };
}

/** Grid keys that persist under `benefits.extended` — see STD_EXTENDED_GRID_FIELD_KEYS. */
export const HOSPITAL_INDEMNITY_EXTENDED_GRID_FIELD_KEYS: ReadonlySet<string> =
  new Set([
    "eligibility",
    "twentyFourHourCoverage",
    "pregnancy",
    "newbornNurseryCare",
    "eligibilityHours",
    "pregnancyWaitingPeriodNote",
  ]);
